import L from "leaflet";
import { GeoJSON, useMap, Polygon, CircleMarker, Tooltip } from "react-leaflet";
import axios from "axios";
import React, { forwardRef, useImperativeHandle } from 'react';
import * as turf from '@turf/turf';
const vegetationClassColors = {
    '0.1': '#c43d3d',
    '0.2': '#d97706',
    '0.4': '#e0b51b',
    '0.6': '#65993a',
    '0.8': '#176b36',
};
const GeoJson = forwardRef((props, ref) => {
    const [data, setData] = React.useState(null);
    const [center, setCenter] = React.useState({ lat: 49.9714, lng: 82.6059 });
    const [geometryBound, setGeometryBound] = React.useState(null);
    const map = useMap();
    const ref_geo = React.useRef(null);
    const requestSequence = React.useRef(0);
    const activeRequest = React.useRef(null);
    useImperativeHandle(ref, () => ({
        changeUrl: get_data
    }));
    React.useEffect(() => {
        get_data(props.url, props.token, center);
        return () => {
            var _a;
            requestSequence.current += 1;
            (_a = activeRequest.current) === null || _a === void 0 ? void 0 : _a.abort();
        };
    }, [props.url, props.token, props.createBoundExtend]);
    React.useEffect(() => {
        if (!data)
            return;
        const geojsonObject = L.geoJSON(data);
        const bounds = geojsonObject.getBounds();
        // Empty NoData layers have no valid extent; retain the current view.
        if (!bounds.isValid())
            return;
        const expression = {
            padding: { x: 30, y: 30 },
            maxZoom: 17
        };
        map.flyToBounds(bounds, expression);
    }, [data]);
    const get_data = (url, token, center) => {
        var _a;
        // Invalidate immediately, including imperative URL changes before effects run.
        const sequence = ++requestSequence.current;
        (_a = activeRequest.current) === null || _a === void 0 ? void 0 : _a.abort();
        const controller = new AbortController();
        activeRequest.current = controller;
        const isCurrent = () => sequence === requestSequence.current && !controller.signal.aborted;
        props.setLoad(Boolean(url && token));
        setData(null);
        setGeometryBound(null);
        setCenter(center);
        if (url && token) {
            axios.get(url, {
                signal: controller.signal,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
                .then((responce) => {
                var _a;
                if (!isCurrent())
                    return;
                if (responce.data) {
                    setData(responce.data);
                    if (props.createBoundExtend) {
                        const allPolygons = ((_a = responce.data.features) !== null && _a !== void 0 ? _a : [])
                            .filter((feature) => { var _a, _b; return ((_a = feature.geometry) === null || _a === void 0 ? void 0 : _a.type) === 'Polygon' || ((_b = feature.geometry) === null || _b === void 0 ? void 0 : _b.type) === 'MultiPolygon'; });
                        if (allPolygons.length === 0)
                            return;
                        try {
                            const allPolygonGeometries = allPolygons.map((polygon) => polygon.geometry);
                            const unioned = allPolygonGeometries.length > 1 ? allPolygonGeometries.reduce((acc, curr) => {
                                return turf.union(acc, curr);
                            }) : turf.feature(allPolygonGeometries[0]);
                            setGeometryBound(unioned.geometry);
                        }
                        catch (_b) {
                            setGeometryBound(null);
                        }
                    }
                }
            })
                .catch(() => {
                if (!isCurrent())
                    return;
                setData(null);
                setGeometryBound(null);
            })
                .finally(() => { if (isCurrent())
                props.setLoad(false); });
        }
        else {
            props.setLoad(false);
        }
    };
    const get_polygon = (coords) => {
        // Проверяем, является ли первый элемент массивом (вложенная структура) или числом (простая координата)
        if (coords.length === 0)
            return [];
        const firstElement = coords[0];
        // Если первый элемент - это число, значит coords это массив чисел [lng, lat]
        if (typeof firstElement === 'number') {
            return [{
                    lat: coords[1],
                    lng: coords[0]
                }];
        }
        // Если первый элемент - массив, обрабатываем каждую координату
        return coords.map((coord) => {
            // coord должен быть [lng, lat]
            if (Array.isArray(coord) && coord.length >= 2) {
                return {
                    lat: coord[1],
                    lng: coord[0]
                };
            }
            // На случай невалидных данных
            return { lat: 0, lng: 0 };
        });
    };
    const get_color = (value, inner, isgrey = false) => {
        var _a;
        let options = {
            color: '#2d8659', // ← ЗЕЛЁНЫЙ вместо #127147
            stroke: false,
            fillOpacity: 1,
            strokeWidth: 0,
            fillRule: 'evenodd'
        };
        if (isgrey == true) {
            options.color = 'white';
            return options;
        }
        if (inner.fill !== undefined) {
            options.color = inner.fill;
            if (inner.fill == 'white') {
                options.color = 'white';
                options.stroke = true;
                options.fillOpacity = 0;
                options.strokeWidth = 1;
            }
        }
        else {
            if (value == 100) {
                options.color = 'white';
                options.stroke = true;
                options.fillOpacity = 0;
                options.strokeWidth = 1;
            }
            else {
                // GeoJSON usually carries class codes, not raw index values.
                const classColor = (_a = Object.entries(vegetationClassColors)
                    .find(([classValue]) => Math.abs(value - Number(classValue)) < 1e-6)) === null || _a === void 0 ? void 0 : _a[1];
                if (classColor) {
                    options.color = classColor;
                }
                else if (value <= 0.1) {
                    options.color = '#c43d3d';
                }
                else if (value <= 0.2) {
                    options.color = '#d97706';
                }
                else if (value < 0.4) {
                    options.color = '#e0b51b';
                }
                else if (value < 0.6) {
                    options.color = '#65993a';
                }
                else {
                    options.color = '#176b36';
                }
            }
        }
        return options;
    };
    // if (data) {
    //     const geojsonObject = L.geoJSON(data);
    //     let bounds: LatLngBounds = geojsonObject.getBounds();
    //     let expression: FitBoundsOptions = {
    //         padding: {
    //             x: 30, y: 30
    //         } as Point,
    //         maxZoom: 17
    //     };
    //     map.flyToBounds(bounds as LatLngBoundsExpression, expression);
    // }
    return (React.createElement(React.Fragment, null, data
        &&
            (React.createElement(React.Fragment, null,
                geometryBound && (React.createElement(Polygon, { positions: get_polygon((geometryBound.type === 'MultiPolygon'
                        ? geometryBound.coordinates[0][0]
                        : geometryBound.coordinates[0])), pathOptions: {
                        color: 'white',
                        stroke: true,
                        fillOpacity: 0,
                        strokeWidth: 1
                    } })),
                props.draw ? (data.features
                    .filter(f => f.geometry !== null)
                    .map((f, index) => {
                    var _a, _b, _c;
                    let isgrey = false;
                    if (props.filterId && props.filterId.length > 0) {
                        isgrey = !((_a = props.filterId) === null || _a === void 0 ? void 0 : _a.includes(((_b = f.id) !== null && _b !== void 0 ? _b : '').toString()));
                    }
                    let options = undefined;
                    if (f.id !== undefined && f.id !== null) {
                        options = get_color(f.id, f.properties, isgrey);
                    }
                    let poligons = [];
                    if (f.geometry) {
                        if (f.geometry.type == "Polygon") {
                            // Polygon ring 0 is the shell; remaining rings are holes.
                            poligons = f.geometry.coordinates.map((ring) => get_polygon(ring));
                        }
                        else if (f.geometry.type == "MultiPolygon") {
                            // Keep each polygon's shell and all its interior rings.
                            poligons = f.geometry.coordinates.map((polygon) => polygon.map((ring) => get_polygon(ring)));
                        }
                    }
                    if (f.geometry.type == "Polygon" || f.geometry.type == "MultiPolygon") {
                        return options !== undefined
                            ?
                                React.createElement(Polygon, { key: `polygon_${index}`, pathOptions: options, positions: poligons })
                            :
                                React.createElement(Polygon, { key: `polygon_${index}`, positions: poligons });
                    }
                    else if (f.geometry.type == "Point") {
                        return React.createElement(CircleMarker, { key: `point_${index}`, center: [f.geometry.coordinates[1], f.geometry.coordinates[0]], radius: 5, stroke: false, color: "white", fillOpacity: 1 }, ((_c = f.properties) === null || _c === void 0 ? void 0 : _c.tooltip) && (React.createElement(Tooltip, null, f.properties.tooltip)));
                    }
                }))
                    : (React.createElement(GeoJSON, { ref: ref_geo, data: data, style: {
                            color: data.features.some(x => x.geometry.type == 'LineString') ? '#ffc107' : 'white',
                            fillColor: '#ffc107',
                            stroke: true,
                            fillOpacity: props.spectralRasterActive ? 0 : 0.3
                        } }))))));
});
export default GeoJson;
